import { createRouter, createWebHistory } from 'vue-router'
import { AdminShell } from '../layouts'
import {
  Promotions,
  LlmCalls,
  LlmConversations,
  LlmTurnTimelines,
  LlmTurnTimelineDetail,
  LlmVideoMatchDetail,
  LlmRunVideoMatch,
  LlmCost,
  LlmModes,
  LlmLedger,
  LlmProviderDetail,
  LlmProvidersList,
  LlmReconciliation,
  LlmRuntime,
  LlmSection,
  ModelCapabilities,
  H3VideoConfig,
  GenerationRecords,
  GenerationRecordProcess,
  TagManagement,
  CreatorProjects,
  CreatorProjectDetail,
  CreatorProjectLogs,
  IntentVideoPreparation,
  IntentVideoClusters,
  IntentVideoQueue,
  IntentVideoResources,
  IntentVideoRecall,
  IntentVideoLogs,
  IntentVideoH3Queue,
  IntentVideoRequestOrResourceDetail,
  IntentVideoClusterDetail,
  AssetLibrary,
  MediaAssetDetail,
  PromptLibrary,
  EntityLibrary,
  EntityLibraryDetail,
  Login,
  UserDetail,
  BillingDashboard,
  BillingPolicies,
  BillingProducts,
  BillingOrders,
  BillingLedger,
  Users,
  Dashboard,
  AnalyticsOverview,
  ContentAnalytics,
  Ga4Behavior,
  ConversionFunnel,
  Waitlist,
  WaitlistDetail,
  InvitationCodes,
  GameReleases,
  GameManagement,
  SocialVideos,
  HomepageCarousel,
  HomepagePopularPins,
  NovelPacks,
  NovelMedia,
  NovelRuns,
  NovelRunAudit,
  NovelComparison,
} from '../pages/index.js'

/**
 * @param {{ auth: ReturnType<import('../composables/useAdminAuth.js').createAdminAuth> }} opts
 */
export function createAdminRouter({ auth }) {
  const router = createRouter({
    history: createWebHistory(),
    routes: [
      { path: '/login', name: 'login', component: Login, meta: { public: true } },
      {
        path: '/',
        component: AdminShell,
        children: [
          { path: '', redirect: '/waitlist' },
          { path: 'waitlist', name: 'waitlist', component: Waitlist },
          { path: 'waitlist/:id', name: 'waitlist-detail', component: WaitlistDetail, props: true },
          { path: 'invitation-codes', name: 'invitation-codes', component: InvitationCodes },
          { path: 'users', name: 'users', component: Users },
          { path: 'users/:id', name: 'user-detail', component: UserDetail, props: true },
          // 商业化四页各自独立链接:流水里的业务对象会深链到订单页 / 流水页并带上筛选。
          { path: 'billing', redirect: '/billing/dashboard' },
          { path: 'billing/dashboard', name: 'billing-dashboard', component: BillingDashboard },
          { path: 'billing/policies', name: 'billing-policies', component: BillingPolicies },
          { path: 'billing/products', name: 'billing-products', component: BillingProducts },
          { path: 'billing/orders', name: 'billing-orders', component: BillingOrders },
          { path: 'billing/ledger', name: 'billing-ledger', component: BillingLedger },
          { path: 'promotions', name: 'promotions', component: Promotions },
          { path: 'analytics/dashboard', name: 'dashboard', component: Dashboard },
          { path: 'analytics/social-videos', name: 'social-videos', component: SocialVideos },
          { path: 'analytics/conversion-funnel', name: 'conversion-funnel', component: ConversionFunnel },
          { path: 'analytics', name: 'analytics', component: AnalyticsOverview },
          { path: 'analytics/content', name: 'content-analytics', component: ContentAnalytics },
          { path: 'analytics/ga4', name: 'ga4-behavior', component: Ga4Behavior },
          { path: 'generation-records', name: 'generation-records', component: GenerationRecords },
          {
            path: 'generation-records/videos/:id',
            name: 'generation-record-process',
            component: GenerationRecordProcess,
            props: true,
          },
          { path: 'tags', name: 'tag-management', component: TagManagement },
          { path: 'games', name: 'game-management', component: GameManagement },
          { path: 'game-releases', name: 'game-releases', component: GameReleases },
          { path: 'homepage-carousel', name: 'homepage-carousel', component: HomepageCarousel },
          { path: 'homepage-popular-pins', name: 'homepage-popular-pins', component: HomepagePopularPins },
          { path: 'creator-projects', name: 'creator-projects', component: CreatorProjects },
          { path: 'creator-projects/:id', name: 'creator-project-detail', component: CreatorProjectDetail, props: true },
          { path: 'creator-projects/:id/logs', name: 'creator-project-logs', component: CreatorProjectLogs, props: true },
          // 每个工作台都有独立链接；准备状态是新游戏接入视频能力的总入口。
          { path: 'intent-videos', redirect: '/intent-videos/preparation' },
          { path: 'intent-videos/preparation', name: 'intent-video-preparation', component: IntentVideoPreparation },
          { path: 'intent-videos/clusters', name: 'intent-video-clusters', component: IntentVideoClusters },
          { path: 'intent-videos/queue', name: 'intent-video-queue', component: IntentVideoQueue },
          { path: 'intent-videos/resources', name: 'intent-video-resources', component: IntentVideoResources },
          { path: 'intent-videos/recall', name: 'intent-video-recall', component: IntentVideoRecall },
          { path: 'intent-videos/logs', name: 'intent-video-logs', component: IntentVideoLogs },
          { path: 'intent-videos/h3-queue', name: 'intent-video-h3-queue', component: IntentVideoH3Queue },
          // 队列/资源/召回测试点开的是同一件事的两半，url 上用各自的段名，方便按来源定位任务。
          {
            path: 'intent-videos/queue/:requestId',
            name: 'intent-video-queue-detail',
            component: IntentVideoRequestOrResourceDetail,
            props: true,
          },
          {
            path: 'intent-videos/resources/:resourceId',
            name: 'intent-video-resource-detail',
            component: IntentVideoRequestOrResourceDetail,
            props: true,
          },
          {
            path: 'intent-videos/recall/:resourceId',
            name: 'intent-video-recall-detail',
            component: IntentVideoRequestOrResourceDetail,
            props: true,
          },
          {
            path: 'intent-videos/clusters/:clusterId',
            name: 'intent-video-cluster-detail',
            component: IntentVideoClusterDetail,
            props: true,
          },
          { path: 'asset-library', name: 'asset-library', component: AssetLibrary },
          { path: 'asset-library/media/:id', name: 'media-asset-detail', component: MediaAssetDetail, props: true },
          { path: 'prompt-library', name: 'prompt-library', component: PromptLibrary },
          { path: 'entity-library', name: 'entity-library', component: EntityLibrary },
          { path: 'entity-library/:entityId', name: 'entity-library-detail', component: EntityLibraryDetail, props: true },
          { path: 'novel/runs', name: 'novel-runs', component: NovelRuns },
          { path: 'novel/runs/:runId', name: 'novel-run-audit', component: NovelRunAudit, props: true },
          { path: 'novel/comparisons/:comparisonId', name: 'novel-comparison', component: NovelComparison, props: true },
          { path: 'novel/packs', name: 'novel-packs', component: NovelPacks },
          { path: 'novel/media', name: 'novel-media', component: NovelMedia },
          // LLM 后台是 admin-only(比候补名单的 admin|operator 更严)。守卫在 `LlmSection` 这一层,
          // 后面六片往 children 里加自己的路由,自动都被守住。
          {
            path: 'llm',
            component: LlmSection,
            children: [
              { path: '', redirect: '/llm/capabilities' },
              { path: 'capabilities', name: 'model-capabilities', component: ModelCapabilities },
              { path: 'h3-config', name: 'h3-video-config', component: H3VideoConfig },
              { path: 'providers', name: 'llm-providers', component: LlmProvidersList },
              {
                path: 'providers/:name',
                name: 'llm-provider-detail',
                component: LlmProviderDetail,
                props: true,
              },
              { path: 'modes', name: 'llm-modes', component: LlmModes },
              { path: 'runtime', name: 'llm-runtime', component: LlmRuntime },
              { path: 'reconciliation', name: 'llm-reconciliation', component: LlmReconciliation },
              { path: 'cost', name: 'llm-cost', component: LlmCost },
              { path: 'calls', name: 'llm-calls', component: LlmCalls },
              { path: 'conversations', name: 'llm-conversations', component: LlmConversations },
              { path: 'turn-timelines', name: 'llm-turn-timelines', component: LlmTurnTimelines },
              {
                path: 'turn-timelines/:turnId',
                name: 'llm-turn-timeline-detail',
                component: LlmTurnTimelineDetail,
                props: true,
              },
              {
                path: 'runs/:runId/video-match',
                name: 'llm-run-video-match',
                component: LlmRunVideoMatch,
                props: true,
              },
              {
                path: 'conversations/:runId/:conversationKey/turns/:turnId/video-match',
                name: 'llm-video-match-detail',
                component: LlmVideoMatchDetail,
                props: true,
              },
              { path: 'ledger/:userId', name: 'llm-ledger', component: LlmLedger, props: true },
            ],
          },
        ],
      },
      { path: '/:pathMatch(.*)*', redirect: '/waitlist' },
    ],
  })

  router.beforeEach(async (to) => {
    if (to.meta.public) return true
    const sessionValid = await auth.ensureSession()
    if (sessionValid) return true
    return { name: 'login', query: { redirect: to.fullPath } }
  })

  return router
}
